import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useMutation, type UseMutationResult, useQuery, type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import * as enhancedSignals from 'shared/api/biddingSignals';
import type * as fetch from 'shared/api/fetch';
import * as analytics from 'shared/api/analytics';
import { type Direction } from 'shared/api/analytics';
import { HttpStatusCodes } from 'shared/httpStatus';
import useMutationAnalytics from 'shared/hooks/useMutationAnalytics';
import { type EventsStatusesItem, type EventsStatusesTimeSeriesItem } from 'src/biddingSignals/types/eventsStatuses';
export const queryKeys = {
  enhancedSignalsConfigs: 'enhancedSignalsConfigs',
  enhancedSignalsMetadata: 'enhancedSignalsMetadata',
  enhancedSignalsValidateLookupData: 'enhancedSignalsValidateLookupData',
  eventsStatuses: 'eventsStatuses',
  eventsStatusesTimeSeries: 'eventsStatusesTimeSeries',
  eventByTransactionId: 'eventByTransactionId',
  configErrors: 'configErrors'
};
export const useQueryEnhancedSignalsConfigs = clientId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== clientId) {
    t0 = [queryKeys.enhancedSignalsConfigs, clientId];
    t1 = async () => await enhancedSignals.getAllEnhancedSignalsByClient(clientId);
    $[0] = clientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(clientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useQueryEnhancedSignalsById = (clientId, configId, t0) => {
  const $ = _c(12);
  const isEnabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId) {
    t1 = [queryKeys.enhancedSignalsConfigs, clientId, configId];
    t2 = async () => await enhancedSignals.getEnhancedSignalById(clientId, configId);
    $[0] = clientId;
    $[1] = configId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== clientId || $[5] !== configId || $[6] !== isEnabled) {
    t3 = isEnabled && Boolean(clientId) && !Number.isNaN(configId);
    $[4] = clientId;
    $[5] = configId;
    $[6] = isEnabled;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useQuery(t4);
};
export const useMutaionCreateEnhancedSignals = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMutationUpdateEnhancedSignals = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.enhancedSignalsConfigs]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
type BulkUpdateResult = Array<{
  configId: number;
  success: boolean;
  error?: fetch.ClientError;
}>;
export const useMutationBulkUpdateEnhancedSignals = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.enhancedSignalsConfigs]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDeleteEnhancedSignals = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp5,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.enhancedSignalsConfigs]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
type BulkDeleteResult = Array<{
  configId: number;
  success: boolean;
  error?: fetch.ClientError;
}>;
export const useMutationBulkDeleteEnhancedSignals = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp7,
      onSettled: async () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.enhancedSignalsConfigs]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationCreateSignedUrl = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp8
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMutationUploadFile = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp9,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.enhancedSignalsMetadata]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDownloadFile = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMutationAnalyticsEnhancedSignalsEvents = () => {
  return useMutationAnalytics(_temp11);
};
export const useQueryLookupFileMetadata = (clientId, configId, fileName, isEnabled) => {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== clientId || $[1] !== configId || $[2] !== fileName) {
    t0 = [queryKeys.enhancedSignalsMetadata, clientId, configId, fileName];
    t1 = async t2 => {
      const {
        client
      } = t2;
      ;
      try {
        return await enhancedSignals.getLookupFileMetadata(clientId, configId, fileName);
      } catch (t3) {
        const error = t3;
        if ((error as fetch.ClientError).status_code === HttpStatusCodes.NOT_FOUND) {
          client.setQueriesData({
            queryKey: [queryKeys.enhancedSignalsMetadata, clientId, configId, fileName]
          }, _temp12);
          return null as unknown as enhancedSignals.LookupFileMetadataResp;
        }
        throw error;
      }
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = fileName;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  const t2 = Boolean(clientId) && isEnabled;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      throwOnError: false
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};
export const useQueryValidateLookupData = (clientId, accountId, lookupType, worksheetId) => {
  const $ = _c(10);
  const isEnabled = worksheetId !== undefined && accountId !== undefined && accountId !== 0 && Boolean(clientId) && lookupType !== "" && lookupType !== undefined;
  let t0;
  let t1;
  if ($[0] !== accountId || $[1] !== clientId || $[2] !== lookupType || $[3] !== worksheetId) {
    t0 = [queryKeys.enhancedSignalsValidateLookupData, clientId, accountId, lookupType, worksheetId];
    t1 = async () => await enhancedSignals.validateLookupData(clientId, accountId, lookupType, worksheetId ?? 0);
    $[0] = accountId;
    $[1] = clientId;
    $[2] = lookupType;
    $[3] = worksheetId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  let t2;
  if ($[6] !== isEnabled || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      throwOnError: false
    };
    $[6] = isEnabled;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useQuery(t2);
};
export interface EventsStatusesResult {
  data: EventsStatusesItem[];
  totalRows: number;
}
const DEFAULT_ORDER_BY: {
  fields: string[];
  direction: Direction;
} = {
  fields: ['publish_time_timestamp'],
  direction: 'DESC'
};
export interface UseQueryEventsStatusesParams {
  configId: number;
  clientId: number;
  startDate: string;
  endDate: string;
  limit?: number;
  cursor?: number;
  orderBy?: {
    fields: string[];
    direction: Direction;
  };
  transactionIdFilter?: string;
  isEnabled?: boolean;
}
export const useQueryEventsStatuses = params => {
  const $ = _c(14);
  const {
    configId,
    clientId,
    startDate,
    endDate,
    limit: t0,
    cursor: t1,
    orderBy,
    transactionIdFilter: t2,
    isEnabled: t3
  } = params;
  const limit = t0 === undefined ? 20 : t0;
  const cursor = t1 === undefined ? 0 : t1;
  const transactionIdFilter = t2 === undefined ? "" : t2;
  const isEnabled = t3 === undefined ? true : t3;
  const resolvedOrderBy = orderBy ?? DEFAULT_ORDER_BY;
  let t4;
  let t5;
  if ($[0] !== clientId || $[1] !== configId || $[2] !== cursor || $[3] !== endDate || $[4] !== limit || $[5] !== resolvedOrderBy || $[6] !== startDate || $[7] !== transactionIdFilter) {
    t4 = [queryKeys.eventsStatuses, configId, clientId, startDate, endDate, limit, cursor, resolvedOrderBy, transactionIdFilter];
    t5 = async () => {
      const filters = [{
        field: "publish_time",
        operator: "BETWEEN",
        value: [startDate, endDate]
      }];
      if (transactionIdFilter !== "") {
        filters.push({
          field: "transaction_id",
          operator: "LIKE",
          logicalOperator: "AND",
          value: transactionIdFilter
        });
      }
      const response = await analytics.postEnhancedSignalsEvents({
        id: configId.toString(),
        clientId,
        report: "events_statuses",
        collection: "enhanced_signals",
        limit,
        cursor,
        filters,
        orderBy: resolvedOrderBy
      });
      return {
        data: response.data as unknown as EventsStatusesItem[],
        totalRows: response.totalRows
      };
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = cursor;
    $[3] = endDate;
    $[4] = limit;
    $[5] = resolvedOrderBy;
    $[6] = startDate;
    $[7] = transactionIdFilter;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  const t6 = isEnabled && configId > 0 && clientId > 0 && startDate !== "" && endDate !== "";
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
      throwOnError: false
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return useQuery(t7);
};
export interface EventsStatusesTimeSeriesResult {
  data: EventsStatusesTimeSeriesItem[];
  totalRows: number;
}
export const useQueryEventsStatusesTimeSeries = (configId, clientId, startDate, endDate, t0) => {
  const $ = _c(10);
  const isEnabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId || $[2] !== endDate || $[3] !== startDate) {
    t1 = [queryKeys.eventsStatusesTimeSeries, configId, clientId, startDate, endDate];
    t2 = async () => {
      const filters = [{
        field: "publish_time",
        operator: "BETWEEN",
        value: [startDate, endDate]
      }];
      const response = await analytics.postEnhancedSignalsEvents({
        id: configId.toString(),
        clientId,
        report: "events_statuses_time_series",
        collection: "enhanced_signals",
        filters,
        orderBy: {
          fields: ["publish_time"],
          direction: "ASC"
        }
      });
      return {
        data: response.data as unknown as EventsStatusesTimeSeriesItem[],
        totalRows: response.totalRows
      };
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = endDate;
    $[3] = startDate;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  const t3 = isEnabled && configId > 0 && clientId > 0 && startDate !== "" && endDate !== "";
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      refetchOnWindowFocus: false,
      throwOnError: false
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};
export interface ConfigErrorItem {
  client_id: string;
  config_id: string;
  details: string;
  error_code: string;
  message_id: string;
  publish_time: string;
  status: string;
}
export interface ConfigErrorsResult {
  data: ConfigErrorItem[];
  totalRows: number;
}
export const useQueryConfigErrors = (configId, clientId, t0) => {
  const $ = _c(8);
  const isEnabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId) {
    t1 = [queryKeys.configErrors, configId, clientId];
    t2 = async () => {
      const response = await analytics.postEnhancedSignalsEvents({
        id: configId.toString(),
        clientId,
        report: "config_errors",
        collection: "enhanced_signals"
      });
      return {
        data: response.data as unknown as ConfigErrorItem[],
        totalRows: response.totalRows
      };
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = isEnabled && configId > 0 && clientId > 0;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      refetchOnWindowFocus: false,
      throwOnError: false
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useQueryEventByTransactionId = (configId, clientId, transactionId, t0) => {
  const $ = _c(9);
  const isEnabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId || $[2] !== transactionId) {
    t1 = [queryKeys.eventByTransactionId, configId, clientId, transactionId];
    t2 = async () => {
      const filters = [{
        field: "transaction_id",
        operator: "=",
        value: transactionId
      }];
      const response = await analytics.postEnhancedSignalsEvents({
        id: configId.toString(),
        clientId,
        report: "events_statuses",
        collection: "enhanced_signals",
        limit: 1,
        filters
      });
      return (response.data?.[0] ?? null) as unknown as EventsStatusesItem | null;
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = transactionId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = isEnabled && configId > 0 && clientId > 0 && transactionId !== "";
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      refetchOnWindowFocus: false,
      throwOnError: false
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
};
async function _temp(data) {
  return await enhancedSignals.createEnhancedSignals(data);
}
async function _temp2(data) {
  return await enhancedSignals.updateEnhancedSignals(data);
}
async function _temp3(config) {
  ;
  try {
    await enhancedSignals.updateEnhancedSignals(config);
    return {
      configId: config.id,
      success: true
    };
  } catch (t0) {
    const error = t0;
    return {
      configId: config.id,
      success: false,
      error: error as fetch.ClientError
    };
  }
}
async function _temp4(configs) {
  const deletePromises = configs.map(_temp3);
  return await Promise.all(deletePromises);
}
async function _temp5(data) {
  return await enhancedSignals.deleteEnhancedSignals(data);
}
async function _temp6(config) {
  ;
  try {
    await enhancedSignals.deleteEnhancedSignals(config);
    return {
      configId: config.configId,
      success: true
    };
  } catch (t0) {
    const error = t0;
    return {
      configId: config.configId,
      success: false,
      error: error as fetch.ClientError
    };
  }
}
async function _temp7(configs) {
  const deletePromises = configs.map(_temp6);
  return await Promise.all(deletePromises);
}
async function _temp8(data) {
  return await enhancedSignals.createSignedUrl(data);
}
async function _temp9(data) {
  return await enhancedSignals.uploadFile(data);
}
async function _temp10(data) {
  return await enhancedSignals.downloadFile(data);
}
async function _temp11(data) {
  return await analytics.postEnhancedSignalsEvents(data);
}
function _temp12() {
  return null;
}